import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {AbortController | undefined} */
  #anchorController;

  /** @type {ResizeObserver | undefined} */
  #anchorObserver;

  /** @type {string | undefined} */
  #anchorColorClass;

  /** Keeps an opted-in dialog aligned with the floating header, including the on-screen keyboard. */
  #positionAtHeader = () => {
    if (!this.hasAttribute('data-header-search')) return;

    const header = document.querySelector('#header-component');
    const row = header?.querySelector('.header__row--top');
    if (!(row instanceof HTMLElement)) return;

    const rect = row.getBoundingClientRect();
    const { dialog } = this.refs;
    const viewport = window.visualViewport;
    dialog.style.setProperty('--header-search-left', `${rect.left}px`);
    dialog.style.setProperty('--header-search-width', `${rect.width}px`);
    dialog.style.setProperty('--header-search-top', `${Math.max(rect.bottom, viewport?.offsetTop ?? 0)}px`);
    dialog.style.setProperty('--header-search-viewport-bottom', `${(viewport?.offsetTop ?? 0) + (viewport?.height ?? window.innerHeight)}px`);
    dialog.style.setProperty('--header-search-radius', getComputedStyle(row).borderTopLeftRadius);

    // The dialog lives outside the header's color context in the document shell.
    const colorClass = Array.from(row.classList).find((name) => name.startsWith('color-'));
    if (this.#anchorColorClass) dialog.classList.remove(this.#anchorColorClass);
    if (colorClass) dialog.classList.add(colorClass);
    this.#anchorColorClass = colorClass;
  };

  #stopPositioning() {
    this.#anchorController?.abort();
    this.#anchorObserver?.disconnect();
  }

  #startPositioning() {
    if (!this.hasAttribute('data-header-search')) return;
    this.#stopPositioning();
    this.#positionAtHeader();
    this.#anchorController = new AbortController();
    const { signal } = this.#anchorController;
    window.addEventListener('resize', this.#positionAtHeader, { signal });
    window.visualViewport?.addEventListener('resize', this.#positionAtHeader, { signal });
    window.visualViewport?.addEventListener('scroll', this.#positionAtHeader, { signal });
    this.#anchorObserver = new ResizeObserver(this.#positionAtHeader);
    const header = document.querySelector('#header-component');
    if (header) this.#anchorObserver.observe(header);
    this.refs.dialog.addEventListener('close', () => this.#stopPositioning(), { signal });
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    this.#stopPositioning();
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#startPositioning();
    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    this.#stopPositioning();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
